// Modal.jsx

import React, { useEffect, useRef, useState } from 'react';

// Device Detection
import { useIsMobile } from 'hooks/useDeviceDetect';

// CSS
import './Modal.css';

// Icons
import Icon from 'svg/Icon';

// Components
import ReactDOM from 'react-dom';

/**
 * Simple global stack manager for modals opened in this tab.
 * - Tracks order of open modals.
 * - Returns the current stack index for a given id.
 */
const ModalStack = (() => {
  const stack = [];
  return {
    add(id) {
      if (!stack.includes(id)) stack.push(id);
    },
    remove(id) {
      const i = stack.indexOf(id);
      if (i > -1) stack.splice(i, 1);
    },
    indexOf(id) {
      return stack.indexOf(id); // -1 if not present
    },
    hasAny() {
      return stack.length > 0;
    },
  };
})();

/** True while any Modal instance is open in this tab. */
export function hasOpenModals() {
  return ModalStack.hasAny();
}

/**
 * Modal Component
 *
 * - On desktop, renders a centered dialog.
 * - On mobile, renders a sliding bottom sheet.
 * 
 * @param {string} iconKey - Optional key for the header icon.
 * @param {string} iconResource - Optional image URL/import; overrides iconKey.
 * @param {object} title - Optional title text.
 * @param {boolean} isOpen - Controls visibility.
 * @param {Function} onClose - Called when the overlay or X is clicked.
 * @param {Function} onBack - When provided, renders a back control in place of the header icon.
 * @param {ReactNode} children - Modal body content.
 * @param {number|string} width - CSS width (number ⇒ px | string).
 * @param {number|string} height - CSS height (number ⇒ px | string).
 * @param {number|string} maxHeight - CSS max-height (number ⇒ px | string).
 * @param {number|string} minHeight - CSS min-height (number ⇒ px | string).
 * @param {number|string} top - CSS top (overrides centering if provided).
 * @param {number|string} left - CSS left (overrides centering if provided).
 * @param {string} padding - Body padding (default '10px').
 * @param {number|string} borderRadius - CSS border radius (number ⇒ px | string).
 * @param {boolean} showHeader - Show the header row (default true).
 * @param {boolean} enableClose - Show the close button (default true).
 * @param {boolean} showOverlay - Show the dimming overlay (default true).
 * @param {object} contentRef - Ref for the modal content element.
 * 
 * @returns {ReactNode} Rendered modal component.
 */
const Modal = ({
  iconKey,
  iconResource,
  title,
  isOpen,
  onClose,
  onBack,
  children,
  width,
  height,
  maxHeight,
  minHeight,
  top,
  left,
  padding = '10px',
  borderRadius,
  showHeader = true,
  enableClose = true,
  showOverlay = true,
  contentRef,
}) => {

  // ----- REFS ----- //

  // Stable id for this instance

  const idRef = useRef(Symbol('modal'));

  // ----- DEVICE DETECTION ----- //

  const isMobile = useIsMobile();

  // ----- STATE ----- //

  // State

  const [isMounted, setIsMounted] = useState(false);
  const [isAnimatingOpen, setIsAnimatingOpen] = useState(false);

  // ----- EFFECTS ----- //

  /**
   * Effect to handle modal open/close animations and stack registration.
   */

  useEffect(() => {
    const modalId = idRef.current;
    if (isOpen) {
      setIsMounted(true);

      // Register into stack on open
      ModalStack.add(modalId);

      // Two rAFs = one full paint cycle:
      requestAnimationFrame(() => {
        requestAnimationFrame(() => setIsAnimatingOpen(true));
      });
    } else {
      setIsAnimatingOpen(false);
      const t = setTimeout(() => setIsMounted(false), 300);

      // Deregister from stack when fully closed/unmounted
      return () => {
        clearTimeout(t);
        ModalStack.remove(modalId);
      };
    }
    // Cleanup in case component unmounts while open
    return () => {
      ModalStack.remove(modalId);
    };
  }, [isOpen]);

  // Lock background scroll while any modal in this stack is open.
  useEffect(() => {
    if (!isOpen) return undefined;

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    return () => {
      if (!ModalStack.hasAny()) {
        document.body.style.overflow = previousOverflow;
      }
    };
  }, [isOpen]);

  // ----- HANDLERS ----- //

  /**
   * handleClose
   *
   * Closes the current surface.
   */
  const handleClose = () => {
    if (enableClose && onClose) {
      onClose();
    }
  };

  // Nothing rendered while unmounted
  if (!isMounted) return null;

  /**
   * Stack-aware z-indexes
   * Base values mirror your previous CSS (999 / 1000) and then step upward.
   */
  const stackIndex = Math.max(0, ModalStack.indexOf(idRef.current));
  const overlayZ = 999 + stackIndex * 2;
  const contentZ = 1000 + stackIndex * 2;

  // ----- UTILITIES ----- //

  /** 
   * Dynamic styles
   */

  const toCssValue = (value) => (typeof value === 'number' ? `${value}px` : value);
  const hasPositionOverride = !isMobile && (top !== undefined || left !== undefined);
  // Clamp desktop dialogs so fixed widths (900–1020px) fit iPad / small viewports.
  const desktopMaxWidth = 'min(100vw - 32px, 100%)';

  // ----- RENDER ----- //

  /** 
   * Render via page
   */

  return ReactDOM.createPortal(
    <>

      {/* OVERLAY */}
      {showOverlay && (
        <div
          className="modal-overlay"
          style={{
            opacity: 1,
            zIndex: overlayZ, // stack-aware
          }}
          onClick={handleClose}
        />
      )}

      {/* CONTENT */}
      <div
        className={`modal-content ${isMobile ? 'mobile' : 'desktop'} ${isAnimatingOpen ? 'open' : 'closed'}`}
        style={{
          borderRadius: borderRadius !== undefined ? toCssValue(borderRadius) : undefined,
          height: height !== undefined ? toCssValue(height) : undefined,
          left: hasPositionOverride && left !== undefined ? toCssValue(left) : undefined,
          maxHeight: maxHeight !== undefined
            ? toCssValue(maxHeight)
            : (isMobile && !height ? 'min(90vh, 90dvh)' : undefined),
          maxWidth: isMobile ? undefined : desktopMaxWidth,
          minHeight: minHeight !== undefined ? toCssValue(minHeight) : undefined,
          top: hasPositionOverride && top !== undefined ? toCssValue(top) : undefined,
          transform: hasPositionOverride ? 'none' : undefined,
          width: width !== undefined ? toCssValue(width) : undefined,
          zIndex: contentZ,
        }}
        ref={contentRef}
        onClick={(e) => e.stopPropagation()}
      >
        {showHeader && (

          <div className="modal-header">

            {onBack ? (
              <button
                className="modal-back-button"
                type="button"
                aria-label="Back"
                onClick={onBack}
              >
                <Icon
                  iconKey="arrow_back"
                  width={20}
                  height={20}
                  color="currentColor"
                />
              </button>
            ) : iconResource ? (
              <div className="modal-icon">
                <img
                  alt=""
                  className="modal-icon-resource"
                  draggable={false}
                  src={iconResource}
                />
              </div>
            ) : iconKey ? (
              <div className="modal-icon">
                <Icon
                  iconKey={iconKey}
                  width={20}
                  height={20}
                  color="currentColor"
                />
              </div>
            ) : null}

            {/* TITLE */}
            <div className="modal-title">
              {title}
            </div>

          </div>
        )}

        {/* CLOSE BUTTON */}
        {enableClose && (
          <button
            className="modal-close-button"
            type="button"
            aria-label="Close"
            onClick={onClose}
          >
            <Icon
              iconKey="close"
              color="currentColor"
              width="26"
              height="26"
            />
          </button>
        )}

        {/* BODY */}
        <div className="modal-body">

          {/* BODY INNER */}
          <div
            className="modal-body-inner"
            style={{
              padding,
            }}
          >
            {children}
          </div>
        </div>

      </div>
    </>,
    document.getElementById('portal-root') || document.body,
  );
};

// Dev Tools
Modal.displayName = 'Modal';

export default Modal;
